import { commonAppData, IconList } from "../../../store/store.ts";
import { devlog } from '../../../logic/utils.ts';
import '../../overlays/as-tooltip.ts';
import styleText from './as-icon-button.css?raw';
/**
 * # AsIconButton Component
 * 
 * A reusable icon button component that provides a consistent UI for controls
 * across the application suite. Features a customizable icon button with tooltip support.
 * 
 * ## Features
 * - Icon-based button with hover effects
 * - Integrated tooltip with customizable placement
 * - Support for light/dark icon variants
 * - Emits events when clicked
 * - Fully customizable via attributes
 * - Supports both direct filenames and icon names from ICON_LIST
 * 
 * ## Component Hierarchy
 * ```
 * AsIconButton (as-icon-button)
 * ├── button - The clickable button element
 * │   └── img - The icon image
 * └── as-tooltip - Tooltip component that appears on hover (optional)
 * ```
 * 
 * ## Attributes
 * - `file-name`: Direct name of the icon image file
 * - `abs-img-path`: Absolute path to the icon image file (highest priority)
 * - `icon-set`: Name of the icon set from ICON_LIST
 * - `icon-variant`: Variant of the icon from ICON_LIST (default: 'default')
 * - `button-id`: Optional ID for the button element
 * - `black`: When present, applies dark styling to the icon (filter: brightness(0))
 * - `tooltip-default-text`: Default text content for the tooltip
 * - `tooltip-dict-key`: Localization key for tooltip text
 * - `tooltip-placement`: Position of the tooltip (top, right, bottom, left, etc.)
 * - `name`: Optional name for the button, used for event identification // button-key  button-id
 * - `disable-tooltips`: When present, prevents tooltips from showing
 * 
 * ## CSS Custom Properties
 * - `--icon-btn-padding`: Vertical padding (default: 0.375rem)
 * - `--icon-btn-radius`: Border radius (default: 0.5rem)
 * - `--icon-btn-size`: Button height and width (default: 2.5rem)
 * - `--icon-btn-bg-col`: Hover background color (default: #002738)
 * - `--icon-btn-hover-bg-col`: Custom hover background color (default: var(--icon-btn-bg-col, #002738))
 * - `--icon-btn-img-filter`: Optional filter to apply to the image on hover (default: none)
 * 
 * ## Events
 * ### Emitted Events
 * - `as-button-click`: Dispatched when the button is clicked
 *   - Detail: `{ buttonName: string | null }` - The name of the button if set
 *   - Expected detail: `{ buttonName: string, iconVariant: string }`
 * 
 * ## Usage Examples
 * 
 * ### Basic Usage with Direct Filename
 * ```html
 * <as-icon-button 
 *   file-name="home.png" 
 *   tooltip-default-text="Go to Home">
 * </as-icon-button>
 * ```
 * 
 * ### With Absolute Image Path
 * ```html
 * <as-icon-button 
 *   abs-img-path="/path/to/images/home-icon.png" 
 *   tooltip-default-text="Go to Home">
 * </as-icon-button>
 * ```
 * 
 * ### With Icon Set
 * ```html
 * <as-icon-button 
 *   icon-set="toggle-workpiece-view"
 *   icon-variant="default"
 *   tooltip-default-text="Toggle Workpiece">
 * </as-icon-button>
 * ```
 * 
 * ### With Custom Tooltip Placement
 * ```html
 * <as-icon-button 
 *   file-name="settings.png" 
 *   tooltip-default-text="Settings" 
 *   tooltip-placement="bottom-right">
 * </as-icon-button>
 * ```
 * 
 * ### Dark Icon Variant
 * ```html
 * <as-icon-button 
 *   file-name="help.png" 
 *   tooltip-default-text="Help" 
 *   black>
 * </as-icon-button>
 * ```
 * 
 * ### With Event Name
 * ```html
 * <as-icon-button 
 *   file-name="save.png" 
 *   tooltip-default-text="Save"
 *   name="save">
 * </as-icon-button>
 * ```
 * 
 * ### Handling Click Events
 * ```javascript
 * const iconButton = document.querySelector('as-icon-button');
 * iconButton.addEventListener('as-button-click', (e) => {
 *   devlog('Button clicked!', e.detail.buttonName);
 *   // Handle navigation or action
 * });
 * ```
 * 
 
 * 
 * ## Implementation Notes
 * - Uses shadow DOM for encapsulation
 * - Automatically shows/hides tooltip on pointer enter/leave
 * - Icon images should be designed for both light and dark backgrounds
 * - When the `black` attribute is present and the button is hovered, 
 *   the image filter can be customized with the `--icon-btn-img-filter` CSS variable
 * - Supports both direct filenames and icon sets from ICON_LIST
 * - Prioritizes icon resolution: 1) abs-img-path, 2) icon-set & variant, 3) name attribute, 4) direct file-name
 */

/**
 * Base button component for header controls
 */
export class AsIconButton extends HTMLElement {
  static get observedAttributes() {
    return ['file-name', 'abs-img-path', 'icon-set', 'icon-variant', 'button-id', 'black', 'tooltip-default-text', 'tooltip-dict-key', 'tooltip-placement', 'name', 'disable-tooltips', 'tooltip-flash', 'tooltip-flash-duration', 'tooltip-flash-delay'];
  }

  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private getIconFileName(): string {
    const absImgFilePath = this.getAttribute('abs-img-path');
    const iconSet = this.getAttribute('icon-set');
    const iconVariant = this.getAttribute('icon-variant') || 'default';
    const fileName = this.getAttribute('file-name');
    const buttonName = this.getAttribute('name');

    // Highest priority: If abs-img-path is provided, use it directly
    if (absImgFilePath) {
      return absImgFilePath;
    }

    // Second priority: If icon-set is provided, use it to look up the icon from ICON_LIST
    if (iconSet) {
      const iconConfig = IconList[iconSet as keyof typeof IconList];
      if (iconConfig) {
        if (iconVariant in iconConfig) {
          return iconConfig[iconVariant as keyof typeof iconConfig];
        } else {
          devlog(`Icon variant ${iconVariant} not found for ${iconSet}`, iconConfig);
        }
      } else {
        devlog(`Icon config for ${iconSet} not found in ICON_LIST`);
      }
    }

    // Third priority: If name attribute is provided, try using it as a fallback for icon lookup
    if (buttonName && !iconSet) {
      const iconConfig = IconList[buttonName as keyof typeof IconList];
      if (iconConfig) {
        if (iconVariant in iconConfig) {
          return iconConfig[iconVariant as keyof typeof iconConfig];
        }
      }
    }

    // Last priority: Fall back to direct file-name if provided
    if (fileName) {
      return `${commonAppData.imgDir}/${fileName}`;
    }

    // Default to empty string if no valid source found
    return '';
  }
  
  /**
   * Creates the tooltip HTML based on available attributes
   */
  private getTooltipHTML(): string {
    const dictKey = this.getAttribute('tooltip-dict-key');
    const defaultText = this.getAttribute('tooltip-default-text');
    const placement = this.getAttribute('tooltip-placement') ?? 'bottom';
    const flash = this.getAttribute('tooltip-flash');
    const flashDuration = this.getAttribute('tooltip-flash-duration');
    const flashDelay = this.getAttribute('tooltip-flash-delay');

    if (!dictKey && !defaultText) {
      return '';
    }

    const flashAttr = flash !== null ? 'flash' : '';
    const flashDurationAttr = flashDuration ? `flash-duration="${flashDuration}"` : '';
    const flashDelayAttr = flashDelay ? `flash-delay="${flashDelay}"` : '';

    if (dictKey) {
      return `<as-tooltip placement="${placement}" dict-key="${dictKey}" default-text="${defaultText ?? ''}" ${flashAttr} ${flashDurationAttr} ${flashDelayAttr}></as-tooltip>`;
    } else {
      return `<as-tooltip placement="${placement}" ${flashAttr} ${flashDurationAttr} ${flashDelayAttr}>${defaultText}</as-tooltip>`;
    }
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    this.shadowRoot!.innerHTML = /*html*/`
      <button>
        <img src="${this.getIconFileName()}" />
      </button>
      ${this.getTooltipHTML()}
    `;    

    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsIconButton.sheet
    ];
  }

  connectedCallback() {
    const button = this.shadowRoot!.querySelector('button');
    button?.addEventListener('click', (e) => {
      // Emit original button-click event
      this.dispatchEvent(new CustomEvent('as-button-click', {
        bubbles: true,
        composed: true,
        detail: { 
          buttonName:this.getAttribute('name'),
          // originalEvent: e 
        }
      }));
    });

    // Only set up tooltip events if tooltip exists and tooltips are not disabled
    const tooltipsDisabled = this.hasAttribute('disable-tooltips');
    if ((this.getAttribute('tooltip-default-text') || this.getAttribute('tooltip-dict-key')) && !tooltipsDisabled) {
      button?.addEventListener('pointerenter', this.handleTooltipEnter);
      button?.addEventListener('pointerleave', this.handleTooltipLeave);

      // Set tooltip placement based on attribute if provided
      const tooltipPlacement = this.getAttribute('tooltip-placement');
      if (tooltipPlacement) {
        this.shadowRoot!.querySelector('as-tooltip')?.setAttribute('placement', tooltipPlacement);
      }
    }
  }

  /**
   * Handle tooltip pointer enter events
   */
  private handleTooltipEnter = () => {
    if (!this.hasAttribute('disable-tooltips')) {
      this.shadowRoot!.querySelector('as-tooltip')?.setAttribute('show', 'true');
    }
  }

  /**
   * Handle tooltip pointer leave events
   */
  private handleTooltipLeave = () => {
    this.shadowRoot!.querySelector('as-tooltip')?.removeAttribute('show');
  }

  attributeChangedCallback(name: string, _: string, newValue: string) {
    if (!this.shadowRoot) return;
    
    const button = this.shadowRoot.querySelector('button');
    const img = this.shadowRoot.querySelector('img');
    if (!img) return;

    if (name === 'file-name' || name === 'abs-img-path' || name === 'icon-set' || name === 'icon-variant' || name === 'name') {
      img.src = this.getIconFileName();
    }
    
    if (name === 'button-id') {
      if (button) {
        button.id = newValue;
      }
    }

    if (name === 'disable-tooltips') {
      // Re-setup tooltip event listeners based on new state
      const tooltip = this.shadowRoot.querySelector('as-tooltip');
      if (tooltip) {
        if (newValue !== null) {
          // Tooltips disabled - remove show attribute and event listeners
          tooltip.removeAttribute('show');
          button?.removeEventListener('pointerenter', this.handleTooltipEnter);
          button?.removeEventListener('pointerleave', this.handleTooltipLeave);
        } else {
          // Tooltips enabled - re-add event listeners if tooltip content exists
          if (this.getAttribute('tooltip-default-text') || this.getAttribute('tooltip-dict-key')) {
            button?.addEventListener('pointerenter', this.handleTooltipEnter);
            button?.addEventListener('pointerleave', this.handleTooltipLeave);
          }
        }
      }
    }

    if (name === 'tooltip-default-text') {
      const tooltip = this.shadowRoot.querySelector('as-tooltip');
      if (tooltip) {
        // If we have a dict-key, set the default-text attribute
        if (tooltip.hasAttribute('dict-key')) {
          tooltip.setAttribute('default-text', newValue);
        } else {
          tooltip.textContent = newValue;
        }
      } else if (this.getAttribute('tooltip-dict-key') || newValue) {
        // If tooltip doesn't exist but we have text or key, create it
        const tooltipContainer = document.createElement('div');
        tooltipContainer.innerHTML = this.getTooltipHTML();
        this.shadowRoot.appendChild(tooltipContainer.firstElementChild!);
        
        // Setup event listeners for the new tooltip if not disabled
        if (!this.hasAttribute('disable-tooltips')) {
          button?.addEventListener('pointerenter', this.handleTooltipEnter);
          button?.addEventListener('pointerleave', this.handleTooltipLeave);
        }
      }
    }

    if (name === 'tooltip-dict-key') {
      let tooltip = this.shadowRoot.querySelector('as-tooltip');
      
      if (tooltip) {
        // Update the dict-key on the existing tooltip
        tooltip.setAttribute('dict-key', newValue);
        
        // Ensure default text is set if available
        const defaultText = this.getAttribute('tooltip-default-text');
        if (defaultText) {
          tooltip.setAttribute('default-text', defaultText);
        }
      } else if (newValue) {
        // If tooltip doesn't exist but we have a key, create it
        const tooltipContainer = document.createElement('div');
        tooltipContainer.innerHTML = this.getTooltipHTML();
        this.shadowRoot.appendChild(tooltipContainer.firstElementChild!);
        
        // Setup event listeners for the new tooltip if not disabled
        if (!this.hasAttribute('disable-tooltips')) {
          button?.addEventListener('pointerenter', this.handleTooltipEnter);
          button?.addEventListener('pointerleave', this.handleTooltipLeave);
        }
      }
    }

    if (name === 'tooltip-placement') {
      const tooltip = this.shadowRoot.querySelector('as-tooltip');
      if (tooltip) {
        tooltip.setAttribute('placement', newValue);
      }
    }

    if (name === 'tooltip-flash') {
      const tooltip = this.shadowRoot.querySelector('as-tooltip');
      if (tooltip) {
        if (newValue !== null) {
          tooltip.setAttribute('flash', '');
        } else {
          tooltip.removeAttribute('flash');
        }
      }
    }

    if (name === 'tooltip-flash-duration') {
      const tooltip = this.shadowRoot.querySelector('as-tooltip');
      if (tooltip && newValue) {
        tooltip.setAttribute('flash-duration', newValue);
      }
    }

    if (name === 'tooltip-flash-delay') {
      const tooltip = this.shadowRoot.querySelector('as-tooltip');
      if (tooltip && newValue) {
        tooltip.setAttribute('flash-delay', newValue);
      }
    }
  }
}

customElements.define('as-icon-button', AsIconButton); 
